import { useEffect, useState } from 'react';

export interface HeaderScrollState {
  /** True while the page is scrolled to (or very near) the top. */
  atTop: boolean;
  /** True when the bar should auto-hide (scrolling down, past the wire). */
  hidden: boolean;
}

/**
 * Drives the masthead's scroll behaviour.
 *  - `atTop`  → the wire (ticker) is in view; both ticker and bar show.
 *  - `hidden` → set when scrolling DOWN past a threshold, cleared on scroll UP.
 *
 * The bar consumes `hidden` only on mobile (CSS makes it a no-op on desktop,
 * where the bar stays pinned). The hook itself is breakpoint-agnostic.
 */
export function useHeaderScroll(): HeaderScrollState {
  const [atTop, setAtTop] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = Math.max(0, window.scrollY);
      setAtTop(y <= 8);

      const delta = y - lastY;
      // Ignore sub-pixel jitter and rubber-band overscroll.
      if (Math.abs(delta) > 6) {
        if (delta > 0 && y > 96) {
          setHidden(true); // scrolling down, past the wire
        } else if (delta < 0) {
          setHidden(false); // any upward intent reveals the bar
        }
        lastY = y;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { atTop, hidden };
}
